import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface SetPopularProductRequest {
  product_id: number;
  score: number;
}

export interface RemovePopularProductRequest {
  product_id: number;
}

export interface PopularProduct {
  product_id: number;
  prod_name: string;
  product_short_name: string;
  product_main_image?: string;
  product_price: number;
  currency: string;
  popular_score: number;
  status: number;
  availability: number;
  cat_name?: string;
  sub_cat_name?: string;
  vendor_id?: number;
  vendor_name?: string;
  cod_enabled?: 0 | 1;  // Add this
  cod_charge?: number;   // Add this
}

export interface PopularProductResponse extends ApiResponse<PopularProduct[]> {
  count?: number;
  total?: number;
}

export interface OpenBoxAllSettings {
  product_id?: number;
  open_box_delivery: 0 | 1;
  open_box_charge?: number;
}

export interface ProductSize {
  id: number;
  product_id: number;
  size: string;
  price: number;
  retail_price: number;
  discount_type: number;
  discount: number;
  createddt?: string;
  updatedt?: string;
}

export interface ProductColor {
  id: number;
  product_id: number;
  color: string;
  price: number;
  retail_price: number;
  color_image?: string;
  createddt?: string;
  updatedt?: string;
}

export interface ProductSizeRequest {
  product_id: number;
  size: string;
  price: number;
  retail_price: number;
  discount_type: number;
  discount: number;
  id?: number;
}

export interface ProductColorRequest {
  product_id: number;
  color: string;
  price: number;
  retail_price: number;
  color_image?: File;
  id?: number;
}

// Product Types
export interface Product {
  id?: number;
  product_id?: number;
  prod_name?: string;
  prod_short_name?: string;
  prod_desc?: string;
  status: number;
  product_unit: string;
  product_quantity: number;
  product_size?: string;
  product_color?: string;
  currency: string;
  product_price: number;
  product_retail_price: number;
  discount_type: number;
  product_discount: number;
  cat_id: number;
  sub_cat_id?: number;
  availability: number;
  max_order_quantity: number;
  tax1_name?: string;
  tax1_per?: number;
  tax2_name?: string;
  tax2_per?: number;
  product_stock: number;
  warehouse_stock?: number;
  warehouse_count?: number;
  approval_status?: 'pending' | 'approved' | 'rejected' | 'blocked' | string;
  shipping_charge?: number;
  shipping_model?: number;
  delivery_model?: number | number[];
  product_main_image?: string;
  vendor_id?: number;
  vendor_name?: string;
  price_priority: number;
  cat_name?: string;
  sub_cat_name?: string;
  createddt?: string;
  updateddt?: string;
  relevance?: number;
  popular_score?: number;
  selling_score?: number;
  properties?: Record<string, string>;
  return_limit?: number;
  is_reward?: number;
  reward_points?: number;
  cod_available?: 0 | 1; 

}

export interface ProductImage {
  id: number;
  product_id: number;
  product_image: string;
  thumb_nail: string;
  createddt?: string;
  updateddt?: string;
}

export interface ProductPrice {
  id: number;
  product_id: number;
  unit: string;
  quantity: number;
  price: number;
  retail_price: number;
  discount_type: number;
  discount: number;
  createddt?: string;
  updateddt?: string;
}

export interface ProductProperty {
  property_name: string;
  property_value: string;
}

export interface ProductFormData {
  product_name: string;
  product_short_name: string;
  product_desc?: string;
  status: number;
  product_unit: string;
  product_quantity: number;
  product_size?: string;
  product_color?: string;
  currency: string;
  product_price: number;
  product_retail_price: number;
  discount_type: number;
  product_discount: number;
  cat_id: number;
  sub_cat_id?: number;
  availability: number;
  max_order_quantity: number;
  tax1_name?: string;
  tax1_per?: number;
  tax2_name?: string;
  tax2_per?: number;
  product_stock: number;
  warehouse_stock?: number;
  warehouse_count?: number;
  approval_status?: 'pending' | 'approved' | 'rejected' | 'blocked' | string;
  shipping_charge?: number;
  shipping_model?: number;
  delivery_model?: number | number[];
  vendor_id?: number;
  price_priority: number;
  tax1?: number;
  tax2?: number;
  shipping_price_models?: number;
   cod_available?: 0 | 1;  // ✅ Add this
}

export interface ProductListParams {
  page?: number;
  limit?: number;
  search_str?: string;
  sortby?: string;
  sortorder?: string;
  vendor_id?: number;
}

export interface ProductCountParams {
  search_str?: string;
  vendor_id?: number;
}

export interface ProductDetailParams {
  product_id: number;
}

export interface ProductDeleteParams {
  product_id: number;
}

export interface AddProductRequest extends ProductFormData {
  product_main_image?: File;
 cod_available?: 0 | 1;  // ✅ Change from cod_enabled to cod_available
}

export interface AddProductImageRequest {
  product_id: number;
  product_main_image: File;
  image_id?: number;
}

export interface DeleteProductImageRequest {
  product_id: number;
  image_id: number;
}

export interface ProductPriceRequest {
  product_id: number;
  unit: string;
  quantity: number;
  price: number;
  retail_price: number;
  discount_type: number;
  discount: number;
  id?: number;
  product_size?: string;
}

export interface ProductPropertyRequest {
  product_id: number;
  property_name: string;
  property_value: string;
}

export interface PopularProductRequest {
  product_id: number;
  score: number;
}

// Reward Product Interfaces
export interface SetProductRewardRequest {
  product_id: number;
  is_reward: 0 | 1;
  reward_points?: number;
}

export interface ProductRewardResponse extends ApiResponse<{
  product_id: number;
  is_reward: number;
  reward_points: number;
}> {}

export interface ApiResponse<T = any> {
  error: boolean;
  message: string;
  result?: T;
  data?: T;
  count?: number;
  total?: number;
  total_records?: number;
  productdet?: T[];
  imagesdet?: T[];
  pricedetails?: T[];
  colorlist?: T[];
  produclist?: T[];
  recId?: number;
  insertId?: number;
}

interface ProductDetailResponse extends ApiResponse<Product> {
  images: { product_image: string }[];
  prices: any[];
  colors: any[];
}

export interface FormErrors {
  [key: string]: string;
}

export interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// ─── Bundle Types ────────────────────────────────────────────────────────────

export interface BundleProduct {
  id?: number;
  bundle_id?: number;
  product_id: number;
  product_name?: string;
  product_price?: number;
  product_retail_price?: number;
  product_main_image?: string;
  quantity: number;
  createddt?: string;
  updateddt?: string;
}

export interface Bundle {
  id?: number;
  bundle_id?: number;
  product_id?: number;
  bundle_name: string;
  bundle_short_name: string;
  bundle_desc?: string;
  status: number;
  currency: string;
  bundle_price: number;
  bundle_retail_price: number;
  discount_type: number;
  bundle_discount: number;
  cat_id: number;
  cat_name?: string;
  sub_cat_id?: number;
  availability: number;
  max_order_quantity: number;
  bundle_stock: number;
  bundle_main_image?: string;
  tax1_name?: string;
  tax1_per?: number;
  tax2_name?: string;
  tax2_per?: number;
  shipping_model?: number;
  delivery_model?: number | number[] | string;
  price_priority: number;
  popular_score?: number;
  vendor_id?: number;
  vendor_name?: string;
  createddt?: string;
  updateddt?: string;
  products?: BundleProduct[];
}

export interface BundleFormData {
  bundle_name: string;
  bundle_short_name: string;
  bundle_desc?: string;
  status: number;
  currency: string;
  bundle_price: number;
  bundle_retail_price: number;
  discount_type: number;
  bundle_discount: number;
  cat_id: number;
  cat_name?: string;
  sub_cat_id?: number;
  availability: number;
  max_order_quantity: number;
  bundle_stock: number;
  tax1_name?: string;
  tax1_per?: number;
  tax2_name?: string;
  tax2_per?: number;
  shipping_model?: number;
  delivery_model?: number[];
  price_priority: number;
  popular_score?: number;
  vendor_id?: number;
  products: BundleProduct[];
}

export interface BulkOrderSettings {
  product_id: number;
  bulkorder: 0 | 1;
}

export interface AddBundleRequest extends BundleFormData {
  bundle_main_image?: File;
}

export interface UpdateBundleRequest extends BundleFormData {
  bundle_id: number;
  bundle_main_image?: File;
}

export interface BundleListParams {
  page?: number;
  limit?: number;
  search_str?: string;
  sortby?: string;
  sortorder?: string;
  vendor_id?: number;
}

export interface BundleDeleteParams {
  bundle_id: number;
  product_id: number;
}

// ─── API Configuration ───────────────────────────────────────────────────────

export const API_CONFIG = {
  BASE_URL: (process.env.REACT_APP_SERVER_ENDPOINT || '').replace(/\/$/, ''),
  AUTH_KEY: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',

  ENDPOINTS: {
    PRODUCT_ADD: '/product/add',
    PRODUCT_UPDATE: '/product/update',
    PRODUCT_DELETE: '/product/delete',
    PRODUCT_DETAIL: '/product/find',
    PRODUCT_LIST: '/product/search',
    PRODUCT_COUNT: '/product/searchCount',
    PRODUCT_ADD_IMAGE: '/product/addProductImage',
    PRODUCT_UPDATE_IMAGE: '/product/updateProductImage',
    PRODUCT_DELETE_IMAGE: '/removeextraimage',

    PRODUCT_PRICES: '/product/productprices',
    PRODUCT_PRICE_DETAIL: '/product/pricedetails',

    SET_OPEN_BOX: '/admin/setopenbox',

    PRODUCT_BY_CATEGORY: '/product/productsearchbycatid',
    PRODUCT_BY_CATEGORY_COUNT: '/product/productsearchbycatidcount',
    PRODUCT_BY_SUBCATEGORY: '/product/productsearchbysubcatid',
    PRODUCT_BY_SUBCATEGORY_COUNT: '/product/productsearchbysubcatidcount',

    SET_POPULAR_PRODUCT: '/product/setpopularproducts',
    REMOVE_POPULAR_PRODUCT: '/product/removepopularproduct',
    POPULAR_PRODUCTS: '/product/popularproducts',
    POPULAR_PRODUCTS_COUNT: '/product/popularproductscount',
    POPULAR_PRODUCTS_BY_CAT: '/product/popularproductsbycat',
    POPULAR_PRODUCTS_COUNT_BY_CAT: '/product/popularproductscountbycat',
    POPULAR_PRODUCTS_BY_SUBCAT: '/product/popularproductsbysubcat',
    POPULAR_PRODUCTS_COUNT_BY_SUBCAT: '/product/popularproductscountbysubcat',

    BUNDLE_ADD: '/product/addbundle',
    BUNDLE_UPDATE: '/product/updatebundle',
    BUNDLE_DELETE: '/product/deletebundle',
    BUNDLE_LIST: '/product/listbundle',
    BUNDLE_DETAIL: '/product/bundledetail',
    BUNDLE_COUNT: '/product/bundlecount',

    PRODUCT_ADD_SIZE: '/product/productaddsize',
    PRODUCT_UPDATE_SIZE: '/product/productupdatesize',
    PRODUCT_DELETE_SIZE: '/product/productdeletesize',
    PRODUCT_SIZES: 'product/productprices',
    PRODUCT_SIZE_DETAIL: '/product/sizedetails',

    PRODUCT_ADD_COLOR: '/product/productaddcolor',
    PRODUCT_UPDATE_COLOR: '/product/productupdatecolor',
    PRODUCT_DELETE_COLOR: '/product/productdeletecolor',
    PRODUCT_COLORS: '/product/productcolors',
    PRODUCT_COLOR_DETAIL: '/product/colordetails',

    ADD_PROPERTY: '/product/addproperty',
    DELETE_PROPERTY: '/product/deleteproperty',

    REMOVE_MAIN_IMAGE: '/removeimage',

    SET_PRODUCT_REWARD: '/user/adminreward-product',
    REMOVE_PRODUCT_REWARD: '/user/adminreward-product',

    // ✅ NEW: Fetch delivery models assigned to a product from junction table
    GET_PRODUCT_DELIVERY_MODELS: '/globalsettings/getProductDeliveryModels',
  },
};

// ─── Helper: create FormData from plain object ───────────────────────────────
const createFormData = (data: any): FormData => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (value !== undefined && value !== null) {
      if (value instanceof File) {
        formData.append(key, value);
      } else if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value.toString());
      }
    }
  });
  return formData;
};

// ─── Product API ─────────────────────────────────────────────────────────────
export const productApi = createApi({
  reducerPath: 'productApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_CONFIG.BASE_URL,
    prepareHeaders: (headers, { endpoint }) => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');
      const userRole = localStorage.getItem('user_role');

      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('authkey', API_CONFIG.AUTH_KEY);

      if (userRole === 'vendor') {
        const vendorId = localStorage.getItem('vendor_id');
        if (vendorId) headers.set('vendor-id', vendorId);
      }

      const formDataEndpoints = [
        'addProduct', 'updateProduct', 'addProductImage',
        'updateProductImage', 'addProductColor', 'updateProductColor',
        'addBundle', 'updateBundle',
      ];

      if (!formDataEndpoints.includes(endpoint)) {
        headers.set('Content-Type', 'application/json');
      }

      return headers;
    },
  }),
  tagTypes: ['Product', 'ProductImage', 'ProductPrice', 'ProductColor', 'ProductProperty', 'ProductSize', 'PopularProduct', 'Bundle'],
  endpoints: (build) => ({

    // ── 1. List Products ──────────────────────────────────────────────────
    listProducts: build.query<ApiResponse<Product[]>, ProductListParams>({
      query: ({ page = 1, limit = 10, search_str = '', sortby = 'relevance', sortorder = 'desc', vendor_id } = {}) => {
        const userRole = localStorage.getItem('user_role');
        const loggedVendorId = localStorage.getItem('vendor_id');
        const effectiveVendorId = userRole === 'vendor' && loggedVendorId
          ? Number(loggedVendorId)
          : (vendor_id || undefined);

        return {
          url: API_CONFIG.ENDPOINTS.PRODUCT_LIST,
          method: 'POST',
          body: JSON.stringify({
            start: (page - 1) * limit,
            limit,
            search_str,
            sortby,
            sortorder,
            vendor_id: effectiveVendorId,
          }),
        };
      },
      providesTags: ['Product'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || '',
        result: response.result || response.produclist || [],
        count: response.count || response.total || response.total_records || 0,
      }),
    }),

    // ── 2. Add Product ────────────────────────────────────────────────────
    addProduct: build.mutation<ApiResponse<any>, AddProductRequest>({
      query: (data) => {
        const userRole = localStorage.getItem('user_role');
        const vendorId = localStorage.getItem('vendor_id');
        if (userRole === 'vendor' && vendorId && !data.vendor_id) {
          data.vendor_id = parseInt(vendorId);
        }
        return { url: API_CONFIG.ENDPOINTS.PRODUCT_ADD, method: 'POST', body: createFormData(data) };
      },
      invalidatesTags: ['Product'],
    }),

    // ── 3. Update Product ─────────────────────────────────────────────────
    updateProduct: build.mutation<ApiResponse<any>, UpdateProductRequest>({
      query: (data) => {
        const userRole = localStorage.getItem('user_role');
        const vendorId = localStorage.getItem('vendor_id');
        if (userRole === 'vendor' && vendorId && !data.vendor_id) {
          data.vendor_id = parseInt(vendorId);
        }
        return { url: API_CONFIG.ENDPOINTS.PRODUCT_UPDATE, method: 'POST', body: createFormData(data) };
      },
      invalidatesTags: ['Product'],
    }),

    // ── 4. Delete Product ─────────────────────────────────────────────────
    deleteProduct: build.mutation<ApiResponse<any>, ProductDeleteParams>({
      query: ({ product_id }) => {
        const userRole = localStorage.getItem('user_role');
        const vendorId = localStorage.getItem('vendor_id');
        const body: any = { product_id };
        if (userRole === 'vendor' && vendorId) body.vendor_id = parseInt(vendorId);
        return { url: API_CONFIG.ENDPOINTS.PRODUCT_DELETE, method: 'POST', body: JSON.stringify(body) };
      },
      invalidatesTags: ['Product'],
    }),

    // ── 5. Get Product Count ──────────────────────────────────────────────
    getProductCount: build.query<ApiResponse<{ count: number }>, ProductCountParams>({
      query: ({ search_str = '', vendor_id } = {}) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_COUNT,
        method: 'POST',
        body: JSON.stringify({ search_str, vendor_id: vendor_id || undefined }),
      }),
      providesTags: ['Product'],
    }),

    // ── 6. Get Product Detail ─────────────────────────────────────────────
    getProductDetail: build.query<ProductDetailResponse, { product_id: number }>({
      query: ({ product_id }) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_DETAIL,
        method: 'POST',
        body: JSON.stringify({ product_id }),
      }),
      providesTags: ['Product'],
      transformResponse: (response: any) => {
        const productData = response.productdet?.[0] || response.data || {};
        return {
          error: response.error || false,
          message: response.message || '',
          result: productData,
          images: response.imagesdet || [],
          prices: response.pricedetails || [],
          colors: response.colorlist || [],
        } as any;
      },
    }),

    // ── 7. Add Product Image ──────────────────────────────────────────────
    addProductImage: build.mutation<any, FormData>({
      query: (formData) => ({
        url: '/product/addProductImage',
        method: 'POST',
        body: formData,
      }),
    }),

    updateProductImage: build.mutation<any, { product_id: any; product_main_image: File; image_id: any }>({
      query: ({ product_id, product_main_image, image_id }) => {
        const formData = new FormData();
        formData.append('product_id', product_id.toString());
        formData.append('product_main_image', product_main_image);
        formData.append('image_id', image_id);
        return { url: '/product/updateProductImage', method: 'POST', body: formData };
      },
    }),

    deleteProductImage: build.mutation<any, { product_id: any; image_id: any }>({
      query: ({ product_id, image_id }) => ({
        url: '/product/deleteProductImage',
        method: 'POST',
        body: { product_id, image_id },
        headers: { 'Content-Type': 'application/json' },
      }),
    }),

    // ════════════════════════════════════════════════════════════════════════
    // BUNDLE ENDPOINTS
    // ════════════════════════════════════════════════════════════════════════

    // ── List Bundles ──────────────────────────────────────────────────────
    listBundles: build.query<ApiResponse<Bundle[]>, BundleListParams>({
      query: ({ page = 1, limit = 10, search_str = '', sortby = 'createddt', sortorder = 'desc', vendor_id } = {}) => ({
        url: API_CONFIG.ENDPOINTS.BUNDLE_LIST,
        method: 'POST',
        body: JSON.stringify({
          start: (page - 1) * limit,
          limit,
          search_str,
          sortby,
          sortorder,
          vendor_id: vendor_id || undefined,
        }),
      }),
      providesTags: ['Bundle'],
      transformResponse: (response: any) => {
        const rawData = response.result || response.data || [];

        const transformedBundles: Bundle[] = Array.isArray(rawData)
          ? rawData.map((item: any) => ({
              id: item.bundle_id || item.id,
              bundle_id: item.bundle_id || item.id,
              product_id: item.product_id || item.id,
              bundle_name: item.bundle_name || item.prod_name || item.product_name || '',
              bundle_short_name: item.bundle_short_name || item.prod_short_name || item.product_short_name || '',
              bundle_desc: item.bundle_desc || item.prod_desc || item.product_desc || '',
              bundle_price: parseFloat(item.bundle_price ?? item.product_price ?? 0),
              bundle_retail_price: parseFloat(item.bundle_retail_price ?? item.product_retail_price ?? 0),
              bundle_discount: parseFloat(item.bundle_discount ?? item.product_discount ?? 0),
              discount_type: item.discount_type || 0,
              currency: item.currency || 'AED',
              bundle_stock: parseInt(item.product_stock ?? item.bundle_stock ?? 0, 10),
              bundle_main_image: item.bundle_main_image || item.product_main_image || null,
              status: item.status ?? 1,
              availability: item.availability ?? 1,
              cat_id: item.cat_id || item.category_id || 0,
              cat_name: item.cat_name || item.category_name || '',
              sub_cat_id: item.sub_cat_id || item.subcategory_id,
              max_order_quantity: item.max_order_quantity || 10,
              price_priority: item.price_priority || 1,
              popular_score: item.popular_score || 0,
              tax1_name: item.tax1_name || 'VAT',
              tax1_per: item.tax1_per || 0,
              tax2_name: item.tax2_name || 'GST',
              tax2_per: item.tax2_per || 0,
              shipping_model: item.shipping_model,
              delivery_model: item.delivery_model,
              vendor_id: item.vendor_id,
              vendor_name: item.vendor_name || '',
              createddt: item.createddt || item.created_at || null,
              updateddt: item.updateddt || item.updated_at || null,
              // ✅ FIX: Use bundle_products from backend
              products: Array.isArray(item.bundle_products)
                ? item.bundle_products.map((p: any) => ({
                    product_id: p.id,
                    product_name: p.prod_name || '',
                    product_price: parseFloat(p.product_price || 0),
                    product_retail_price: parseFloat(p.product_retail_price || 0),
                    product_main_image: p.product_main_image || '',
                    quantity: p.quantity || 1,
                  }))
                : [],
            }))
          : [];

        return {
          error: response.error || false,
          message: response.message || '',
          result: transformedBundles,
          count: response.count || response.total || response.total_records || transformedBundles.length || 0,
        };
      },
    }),

    // ── Add Bundle (simplified – no field mapping) ─────────────────────
    addBundle: build.mutation<ApiResponse<any>, AddBundleRequest & { bundle_main_image?: File }>({
      query: (data) => {
        const userRole = localStorage.getItem('user_role');
        const vendorId = localStorage.getItem('vendor_id');
        if (userRole === 'vendor' && vendorId && !data.vendor_id) {
          data.vendor_id = parseInt(vendorId);
        }

        const formData = new FormData();

        // Append all fields exactly as they come from the component
        Object.keys(data).forEach((key) => {
          const value = data[key as keyof typeof data];
          if (value === undefined || value === null) return;

          if (key === 'products') {
            // products is an array of { product_id, quantity }
            formData.append(key, JSON.stringify(value));
          } else if (key === 'bundle_main_image' && value instanceof File) {
            formData.append('product_main_image', value); // backend expects product_main_image
          } else if (typeof value === 'object') {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        });

        // Ensure required fields not in data are added with defaults
        if (!data.product_unit) formData.append('product_unit', 'pcs');
        if (!data.product_quantity) formData.append('product_quantity', '1');
        if (!data.is_bundle) formData.append('is_bundle', '1');
        if (!data.product_size) formData.append('product_size', '');
        if (!data.product_color) formData.append('product_color', '');
        if (!data.shipping_charge) formData.append('shipping_charge', '0');
        if (!data.product_return_limit) formData.append('product_return_limit', '0');

        return {
          url: API_CONFIG.ENDPOINTS.BUNDLE_ADD,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Bundle'],
    }),

    // ── Update Bundle (simplified) ─────────────────────────────────────
    updateBundle: build.mutation<ApiResponse<any>, UpdateBundleRequest & { bundle_main_image?: File }>({
      query: (data) => {
        const userRole = localStorage.getItem('user_role');
        const vendorId = localStorage.getItem('vendor_id');
        if (userRole === 'vendor' && vendorId && !data.vendor_id) {
          data.vendor_id = parseInt(vendorId);
        }

        const formData = new FormData();

        Object.keys(data).forEach((key) => {
          const value = data[key as keyof typeof data];
          if (value === undefined || value === null) return;

          if (key === 'products') {
            formData.append(key, JSON.stringify(value));
          } else if (key === 'bundle_main_image' && value instanceof File) {
            formData.append('product_main_image', value);
          } else if (typeof value === 'object') {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        });

        // Add bundle_id as product_id for backend
        if (data.bundle_id) {
          formData.append('product_id', data.bundle_id.toString());
        }

        // Defaults
        if (!data.product_unit) formData.append('product_unit', 'pcs');
        if (!data.product_quantity) formData.append('product_quantity', '1');
        if (!data.is_bundle) formData.append('is_bundle', '1');

        return {
          url: API_CONFIG.ENDPOINTS.BUNDLE_UPDATE,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Bundle'],
    }),

    // ── Delete Bundle ─────────────────────────────────────────────────────
    deleteBundle: build.mutation<ApiResponse<any>, BundleDeleteParams>({
      query: ({ product_id }) => {
        const userRole = localStorage.getItem('user_role');
        const vendorId = localStorage.getItem('vendor_id');
        const body: any = { product_id };
        if (userRole === 'vendor' && vendorId) body.vendor_id = parseInt(vendorId);
        return { url: API_CONFIG.ENDPOINTS.BUNDLE_DELETE, method: 'POST', body: JSON.stringify(body) };
      },
      invalidatesTags: ['Bundle'],
    }),

    // ── Get Bundle Detail ─────────────────────────────────────────────────
    getBundleDetail: build.query<ApiResponse<Bundle>, { bundle_id: number }>({
      query: ({ bundle_id }) => ({
        url: API_CONFIG.ENDPOINTS.BUNDLE_DETAIL,
        method: 'POST',
        body: JSON.stringify({ bundle_id }),
      }),
      providesTags: ['Bundle'],
      transformResponse: (response: any) => {
        // Optionally transform bundle_products here if needed
        return response;
      },
    }),

    // ── Get Bundle Count ──────────────────────────────────────────────────
    getBundleCount: build.query<ApiResponse<{ count: number }>, { search_str?: string; vendor_id?: number }>({
      query: ({ search_str = '', vendor_id } = {}) => ({
        url: API_CONFIG.ENDPOINTS.BUNDLE_COUNT,
        method: 'POST',
        body: JSON.stringify({ search_str, vendor_id: vendor_id || undefined }),
      }),
      providesTags: ['Bundle'],
    }),

    // ════════════════════════════════════════════════════════════════════════
    // PRICE PACK ENDPOINTS
    // ════════════════════════════════════════════════════════════════════════

    addProductPricePack: build.mutation<any, any>({
      query: (body) => ({
        url: 'product/productaddprice',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }),
    }),

    updateProductPricePack: build.mutation<any, any>({
      query: (body) => ({
        url: 'product/productupdateprice',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }),
    }),

    deleteProductPricePack: build.mutation<any, any>({
      query: (body) => ({
        url: 'product/productdeleteprice',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }),
    }),

    getProductPrices: build.query<ApiResponse<ProductPrice[]>, { product_id: number }>({
      query: ({ product_id }) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_PRICES,
        method: 'POST',
        body: JSON.stringify({ product_id }),
      }),
      providesTags: ['ProductPrice'],
    }),

    getProductPriceDetail: build.query<ApiResponse<ProductPrice>, { product_id: number; id: number }>({
      query: ({ product_id, id }) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_PRICE_DETAIL,
        method: 'POST',
        body: JSON.stringify({ product_id, id }),
      }),
      providesTags: ['ProductPrice'],
    }),

    addProductColor: build.mutation<ApiResponse<any>, ProductColorRequest>({
      query: (data) => ({ url: API_CONFIG.ENDPOINTS.PRODUCT_ADD_COLOR, method: 'POST', body: createFormData(data) }),
      invalidatesTags: ['ProductColor'],
    }),

    updateProductColor: build.mutation<ApiResponse<any>, ProductColorRequest>({
      query: (data) => ({ url: API_CONFIG.ENDPOINTS.PRODUCT_UPDATE_COLOR, method: 'POST', body: createFormData(data) }),
      invalidatesTags: ['ProductColor'],
    }),

    deleteProductColor: build.mutation<ApiResponse<any>, { product_id: number; id: number }>({
      query: ({ product_id, id }) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_DELETE_COLOR,
        method: 'POST',
        body: JSON.stringify({ product_id, id }),
      }),
      invalidatesTags: ['ProductColor'],
    }),

    getProductColors: build.query<ApiResponse<ProductColor[]>, { product_id: number }>({
      query: ({ product_id }) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_COLORS,
        method: 'POST',
        body: JSON.stringify({ product_id }),
      }),
      providesTags: ['ProductColor'],
    }),

    getProductColorDetail: build.query<ApiResponse<ProductColor>, { product_id: number; id: number }>({
      query: ({ product_id, id }) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_COLOR_DETAIL,
        method: 'POST',
        body: JSON.stringify({ product_id, id }),
      }),
      providesTags: ['ProductColor'],
    }),

    getProductsByCategory: build.query<ApiResponse<Product[]>, { cat_id: number; page?: number; limit?: number; vendor_id?: number }>({
      query: ({ cat_id, page = 1, limit = 10, vendor_id }) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_BY_CATEGORY,
        method: 'POST',
        body: JSON.stringify({ cat_id, start: (page - 1) * limit, limit, vendor_id: vendor_id || undefined }),
      }),
      providesTags: ['Product'],
    }),

    getProductsByCategoryCount: build.query<ApiResponse<{ count: number }>, { cat_id: number; vendor_id?: number }>({
      query: ({ cat_id, vendor_id }) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_BY_CATEGORY_COUNT,
        method: 'POST',
        body: JSON.stringify({ cat_id, vendor_id: vendor_id || undefined }),
      }),
      providesTags: ['Product'],
    }),

    getProductsBySubcategory: build.query<ApiResponse<Product[]>, { sub_cat_id: number; page?: number; limit?: number; vendor_id?: number }>({
      query: ({ sub_cat_id, page = 1, limit = 10, vendor_id }) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_BY_SUBCATEGORY,
        method: 'POST',
        body: JSON.stringify({ sub_cat_id, start: (page - 1) * limit, limit, vendor_id: vendor_id || undefined }),
      }),
      providesTags: ['Product'],
    }),

    getProductsBySubcategoryCount: build.query<ApiResponse<{ count: number }>, { sub_cat_id: number; vendor_id?: number }>({
      query: ({ sub_cat_id, vendor_id }) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_BY_SUBCATEGORY_COUNT,
        method: 'POST',
        body: JSON.stringify({ sub_cat_id, vendor_id: vendor_id || undefined }),
      }),
      providesTags: ['Product'],
    }),

    setPopularProduct: build.mutation<ApiResponse<any>, SetPopularProductRequest>({
      query: ({ product_id, score }) => ({
        url: API_CONFIG.ENDPOINTS.SET_POPULAR_PRODUCT,
        method: 'POST',
        body: JSON.stringify({ product_id, score }),
      }),
      invalidatesTags: ['Product', 'PopularProduct'],
    }),

    removePopularProduct: build.mutation<ApiResponse<any>, RemovePopularProductRequest>({
      query: ({ product_id }) => ({
        url: API_CONFIG.ENDPOINTS.REMOVE_POPULAR_PRODUCT,
        method: 'POST',
        body: JSON.stringify({ product_id }),
      }),
      invalidatesTags: ['Product', 'PopularProduct'],
    }),

    getPopularProducts: build.query<PopularProductResponse, { page?: number; limit?: number; search_str?: string; vendor_id?: number }>({
      query: ({ page = 1, limit = 10, search_str = '', vendor_id } = {}) => ({
        url: API_CONFIG.ENDPOINTS.POPULAR_PRODUCTS,
        method: 'POST',
        body: JSON.stringify({ start: (page - 1) * limit, limit, search_str, vendor_id: vendor_id || undefined }),
      }),
      providesTags: ['PopularProduct'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || '',
        result: response.result || response.data || [],
        count: response.count || response.total || 0,
      }),
    }),

    getPopularProductsCount: build.query<ApiResponse<{ count: number }>, { search_str?: string; vendor_id?: number }>({
      query: ({ search_str = '', vendor_id } = {}) => ({
        url: API_CONFIG.ENDPOINTS.POPULAR_PRODUCTS_COUNT,
        method: 'POST',
        body: JSON.stringify({ search_str, vendor_id: vendor_id || undefined }),
      }),
      providesTags: ['PopularProduct'],
    }),

    getPopularProductsByCategory: build.query<ApiResponse<Product[]>, { cat_id: number; page?: number; limit?: number; vendor_id?: number }>({
      query: ({ cat_id, page = 1, limit = 10, vendor_id }) => ({
        url: API_CONFIG.ENDPOINTS.POPULAR_PRODUCTS_BY_CAT,
        method: 'POST',
        body: JSON.stringify({ cat_id, start: (page - 1) * limit, limit, vendor_id: vendor_id || undefined }),
      }),
      providesTags: ['Product'],
    }),

    getPopularProductsByCategoryCount: build.query<ApiResponse<{ count: number }>, { cat_id: number; vendor_id?: number }>({
      query: ({ cat_id, vendor_id }) => ({
        url: API_CONFIG.ENDPOINTS.POPULAR_PRODUCTS_COUNT_BY_CAT,
        method: 'POST',
        body: JSON.stringify({ cat_id, vendor_id: vendor_id || undefined }),
      }),
      providesTags: ['Product'],
    }),

    getPopularProductsBySubcategory: build.query<ApiResponse<Product[]>, { sub_cat_id: number; page?: number; limit?: number; vendor_id?: number }>({
      query: ({ sub_cat_id, page = 1, limit = 10, vendor_id }) => ({
        url: API_CONFIG.ENDPOINTS.POPULAR_PRODUCTS_BY_SUBCAT,
        method: 'POST',
        body: JSON.stringify({ sub_cat_id, start: (page - 1) * limit, limit, vendor_id: vendor_id || undefined }),
      }),
      providesTags: ['Product'],
    }),

    getPopularProductsBySubcategoryCount: build.query<ApiResponse<{ count: number }>, { sub_cat_id: number; vendor_id?: number }>({
      query: ({ sub_cat_id, vendor_id }) => ({
        url: API_CONFIG.ENDPOINTS.POPULAR_PRODUCTS_COUNT_BY_SUBCAT,
        method: 'POST',
        body: JSON.stringify({ sub_cat_id, vendor_id: vendor_id || undefined }),
      }),
      providesTags: ['Product'],
    }),

    addProductProperty: build.mutation<any, { product_id: number; property_name: string; property_value: string }>({
      query: (body) => ({
        url: 'product/addproperty',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }),
    }),

    deleteProductProperty: build.mutation<any, { product_id: number; property_name: string }>({
      query: (body) => ({
        url: 'product/deleteproperty',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }),
    }),

    updateProductDeliveryModels: build.mutation<ApiResponse<any>, { product_id: number; delivery_model_ids: number[] }>({
      query: (data) => ({
        url: '/globalsettings/updateProductDeliveryModels',
        method: 'POST',
        body: JSON.stringify(data),
      }),
      invalidatesTags: ['Product'],
    }),

    // ✅ NEW: Get delivery models assigned to a product from junction table
    getProductDeliveryModels: build.query<ApiResponse<number[]>, { product_id: number }>({
      query: ({ product_id }) => ({
        url: API_CONFIG.ENDPOINTS.GET_PRODUCT_DELIVERY_MODELS,
        method: 'POST',
        body: JSON.stringify({ product_id }),
      }),
    }),

    removeMainImage: build.mutation<ApiResponse<any>, { product_id: number }>({
      query: ({ product_id }) => ({
        url: API_CONFIG.ENDPOINTS.REMOVE_MAIN_IMAGE,
        method: 'POST',
        body: JSON.stringify({ product_id }),
      }),
      invalidatesTags: ['Product'],
    }),

    addProductSize: build.mutation<ApiResponse<any>, ProductSizeRequest>({
      query: (data) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_ADD_SIZE,
        method: 'POST',
        body: JSON.stringify(data),
      }),
      invalidatesTags: ['ProductSize'],
    }),

    updateProductSize: build.mutation<ApiResponse<any>, ProductSizeRequest>({
      query: (data) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_UPDATE_SIZE,
        method: 'POST',
        body: JSON.stringify(data),
      }),
      invalidatesTags: ['ProductSize'],
    }),

    deleteProductSize: build.mutation<ApiResponse<any>, { product_id: number; id: number }>({
      query: ({ product_id, id }) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_DELETE_SIZE,
        method: 'POST',
        body: JSON.stringify({ product_id, id }),
      }),
      invalidatesTags: ['ProductSize'],
    }),

    getProductSizes: build.query<ApiResponse<ProductSize[]>, { product_id: number }>({
      query: ({ product_id }) => ({
        url: API_CONFIG.ENDPOINTS.PRODUCT_SIZES,
        method: 'POST',
        body: JSON.stringify({ product_id }),
      }),
      providesTags: ['ProductSize'],
    }),

    setProductReward: build.mutation<ProductRewardResponse, SetProductRewardRequest>({
      query: ({ product_id, is_reward, reward_points }) => ({
        url: API_CONFIG.ENDPOINTS.SET_PRODUCT_REWARD,
        method: 'POST',
        body: JSON.stringify({ product_id, is_reward, reward_points: is_reward === 1 ? reward_points : undefined }),
      }),
      invalidatesTags: ['Product'],
    }),

    removeProductReward: build.mutation<ProductRewardResponse, { product_id: number }>({
      query: ({ product_id }) => ({
        url: API_CONFIG.ENDPOINTS.REMOVE_PRODUCT_REWARD,
        method: 'POST',
        body: JSON.stringify({ product_id, is_reward: 0 }),
      }),
      invalidatesTags: ['Product'],
    }),

    setOpenBox: build.mutation<ApiResponse<any>, OpenBoxAllSettings>({
      query: (openBoxData) => ({
        url: API_CONFIG.ENDPOINTS.SET_OPEN_BOX,
        method: 'POST',
        body: JSON.stringify(openBoxData),
      }),
      invalidatesTags: ['Product'],
    }),

    setBulkOrder: build.mutation<ApiResponse<any>, BulkOrderSettings>({
      query: (bulkOrderData) => ({
        url: '/user/setProductBulkOrder',
        method: 'POST',
        body: JSON.stringify(bulkOrderData),
      }),
      invalidatesTags: ['Product'],
    }),
  }),
});

export const {
  useListProductsQuery,
  useAddProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useGetProductCountQuery,
  useGetProductDetailQuery,
  useAddProductImageMutation,
  useUpdateProductImageMutation,
  useDeleteProductImageMutation,
  useGetProductPricesQuery,
  useGetProductPriceDetailQuery,
  useAddProductColorMutation,
  useUpdateProductColorMutation,
  useDeleteProductColorMutation,
  useGetProductColorsQuery,
  useGetProductColorDetailQuery,
  useGetProductsByCategoryQuery,
  useGetProductsByCategoryCountQuery,
  useGetProductsBySubcategoryQuery,
  useGetProductsBySubcategoryCountQuery,
  useSetPopularProductMutation,
  useGetPopularProductsQuery,
  useGetPopularProductsCountQuery,
  useGetPopularProductsByCategoryQuery,
  useGetPopularProductsByCategoryCountQuery,
  useGetPopularProductsBySubcategoryQuery,
  useGetPopularProductsBySubcategoryCountQuery,
  useAddProductPropertyMutation,
  useDeleteProductPropertyMutation,
  useRemoveMainImageMutation,
  useAddProductPricePackMutation,
  useUpdateProductPricePackMutation,
  useDeleteProductPricePackMutation,
  useAddProductSizeMutation,
  useRemovePopularProductMutation,
  useUpdateProductSizeMutation,
  useDeleteProductSizeMutation,
  useGetProductSizesQuery,
  useListBundlesQuery,
  useAddBundleMutation,
  useUpdateBundleMutation,
  useDeleteBundleMutation,
  useGetBundleDetailQuery,
  useGetBundleCountQuery,
  useSetProductRewardMutation,
  useRemoveProductRewardMutation,
  useLazyGetBundleDetailQuery,
  useSetOpenBoxMutation,
  useSetBulkOrderMutation,
  useUpdateProductDeliveryModelsMutation,
  // ✅ NEW: Export the lazy query for fetching product delivery models
  useLazyGetProductDeliveryModelsQuery,
} = productApi;
